import { ChangeDetectionStrategy, Component, inject, signal } from '@angular/core';
import { Router, RouterOutlet } from '@angular/router';
import {
  NavigationCancel,
  NavigationCancellationCode,
  NavigationEnd,
  NavigationError,
  NavigationSkipped,
} from '@angular/router';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { filter, take } from 'rxjs/operators';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatButtonModule } from '@angular/material/button';
import { MaintenanceWatcher } from './services/maintenance-watcher.service';
import { AppCheckGateService } from './services/app-check-gate.service';
import { VersionWatcher } from './services/version-watcher.service';
import { message } from './config/messages';

/**
 * The application root, and the boot screen.
 *
 * Guards block rendering while they resolve, and the first navigation into the console
 * has to wait for `notUnderMaintenanceGuard` — which reads the remote kill switch from
 * the `settings` document and gives it up to five seconds before deciding. Until that
 * resolves nothing at all is on screen, so a cold start with a slow or unreachable
 * Firestore showed a blank white page for several seconds and then, with no warning,
 * the console. It looked like the app had failed to start; it had not.
 *
 * The splash covers only the first navigation. Afterwards the shell is rendered and its
 * own progress bar reports work, so an overlay would be intrusive rather than reassuring.
 *
 * Before any of that, the App Check gate: the app initializer holds bootstrap until the
 * first round of attempts has an answer (index.html shows a static splash meanwhile), so
 * this component only ever sees `verified` or `failed`. On `failed` the blocked page
 * replaces the outlet, with the reason and a retry; a retry that succeeds reloads the
 * page, which comes up verified from the token the SDK has now persisted, rather than
 * resuming a first navigation whose guards ran without one.
 *
 * And after it, the version: when `settings/v1.garageApp.web` names a newer build with
 * `forceUpdate` set, the blocking page with a reload replaces everything, the login
 * included (VersionWatcher).
 */
@Component({
  selector: 'app-root',
  standalone: true,
  imports: [RouterOutlet, MatProgressSpinnerModule, MatButtonModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    @if (appCheck.status() === 'failed') {
      <div class="app-boot app-blocked" data-testid="app-check-failed">
        <img src="assets/img/logo_esteso_positivo.svg" alt="Parkingless" />
        <h1>{{ copy.title }}</h1>
        <p>{{ appCheck.failure() === 'network' ? copy.network : copy.attestation }}</p>
        <button mat-flat-button (click)="retryAppCheck()" data-testid="app-check-retry">
          {{ copy.retry }}
        </button>
      </div>
    } @else if (version.state() === 'force') {
      <div class="app-boot app-blocked" data-testid="console-force-update">
        <img src="assets/img/logo_esteso_positivo.svg" alt="Parkingless" />
        <h1>{{ versionCopy.title }}</h1>
        <p>{{ versionCopy.body }}</p>
        <p class="app-blocked-detail">
          {{ versionCopy.versions(version.localVersion, version.remoteVersion() ?? '') }}
        </p>
        <button mat-flat-button (click)="version.reload()" data-testid="console-force-update-reload">
          {{ versionCopy.reload }}
        </button>
      </div>
    } @else {
      @if (booting() || appCheck.status() === 'pending') {
        <div class="app-boot">
          <img src="assets/img/logo_esteso_positivo.svg" alt="Parkingless" />
          <mat-spinner diameter="32" />
        </div>
      }

      <router-outlet />
    }
  `,
  styleUrl: './app.component.scss',
})
export class AppComponent {
  protected readonly booting = signal(true);
  protected readonly appCheck = inject(AppCheckGateService);
  protected readonly copy = message.appCheck;
  protected readonly version = inject(VersionWatcher);
  protected readonly versionCopy = message.versionUpdate;

  protected async retryAppCheck(): Promise<void> {
    await this.appCheck.retry();
    if (this.appCheck.status() === 'verified') {
      window.location.reload();
    }
  }

  constructor() {
    // The kill switch is watched for the life of the app rather than awaited by a guard
    // on every navigation. See MaintenanceWatcher for why.
    inject(MaintenanceWatcher).start();
    this.version.start();

    inject(Router)
      .events.pipe(
        // `NavigationEnd` alone is not enough — a failure must clear the splash too, or
        // the error page renders underneath it. But a guard redirect is not an ending:
        // it reports `NavigationCancel` with code `Redirect` and immediately starts a
        // second navigation, and the first navigation into this app is very often
        // exactly that. Clearing on it would drop the splash and go straight back to the
        // blank screen this exists to prevent.
        filter(
          (event) =>
            event instanceof NavigationEnd ||
            event instanceof NavigationError ||
            event instanceof NavigationSkipped ||
            (event instanceof NavigationCancel &&
              event.code !== NavigationCancellationCode.Redirect &&
              event.code !== NavigationCancellationCode.SupersededByNewNavigation),
        ),
        take(1),
        takeUntilDestroyed(),
      )
      .subscribe(() => this.booting.set(false));
  }
}
